import { TransitionSeries, springTiming } from '@remotion/transitions'
import { Transition } from '../../../shared/utils/remotion/transitionShim'
import React from 'react'
import { AbsoluteFill, Audio, Sequence, interpolate, staticFile, useCurrentFrame } from 'remotion'
import { burstImageTransitionFunctions, transitionAnimationsDropDown } from '../../../data/transitions'
import { generateUniqueId } from '../../../shared/utils/core'
import './fonts.css'
import { SinglePage } from './singlePage'
import { AnimatedSubtitles } from './subtitles'
import { SoundEffects } from './soundEffects'
import { addSound } from './addSound'
import RenderPosterComponentTwo from './renderPosterComp'
import { millisecondsToFrames} from '../../../shared/utils/remotion'
import { FontLoader } from '../../../shared/components/remotion/FontLoader'
import { removeVideoTags } from '../../../shared/utils/remotion/htmlProcessing'
import { easeOutBack } from '../../../shared/utils/VideoGeneration/helperFunctions/easingFunctions'

// import { getAvailableFonts } from "@remotion/google-fonts";
// import { useGlobalContext } from '../../context/globalContext'

interface HtmlProp {
  htmlData: any
  jsonData: any
  scriptDetails: any
  width: number
  height: number
  transitionDurationInFrames: number
  transitionAnimationProp: any
  isDownload: boolean
  burstImagesTransitionAnimationArray: any
  videoStyle: string
  aiAvatar: boolean
}
// TO-DO -> Make this dynamic from user input - Hard coded FPS
const fps = 30
const delayInSeconds = 1

const PremountedAudio: React.FC<{
  src: string
  startFrom: number
  endAt: number
  volume: number | ((f: number) => number)
  premountFor: number
}> = ({ src, startFrom, endAt, volume, premountFor }) => {
  const frame = useCurrentFrame()
  const isPremounting = frame < 0 && frame >= -premountFor

  return (
    <Audio
      src={src}
      startFrom={startFrom}
      endAt={endAt}
      volume={volume}
      pauseWhenBuffering={!isPremounting}
    />
  )
}

const PremountedVoiceOver: React.FC<{
  src: string
  startFrom: number
  volume: number
  playbackRate: number
  premountFor: number
}> = ({ src, startFrom, volume, playbackRate, premountFor }) => {
  const frame = useCurrentFrame()
  const isPremounting = frame < 0 && frame >= -premountFor

  return (
    <Audio
      src={src}
      startFrom={startFrom}
      volume={volume}
      playbackRate={playbackRate}
      pauseWhenBuffering={!isPremounting}
    />
  )
}

// Create a new component for the animated block type text
const AnimatedBlockType: React.FC<{
  text: string;
  width: number;
  height: number;
}> = ({ text, width, height }) => {
  const frame = useCurrentFrame();
  
  // Animation timing
  const appearDuration = 15; // frames to animate in
  const stayDuration = 60; // frames to stay visible
  const disappearDuration = 15; // frames to animate out
  const totalDuration = appearDuration + stayDuration + disappearDuration;
  
  // Calculate Y position based on frame
  let y = 0;
  
  if (frame < appearDuration) {
    // Animate from below screen to final position
    y = interpolate(
      frame,
      [0, appearDuration],
      [height + 100, height - 100],
      { extrapolateRight: 'clamp' }
    );
  } else if (frame < appearDuration + stayDuration) {
    // Stay at position
    y = height - 100;
  } else if (frame < totalDuration) {
    // Animate out downward
    y = interpolate(
      frame,
      [appearDuration + stayDuration, totalDuration],
      [height - 100, height + 100],
      { extrapolateLeft: 'clamp' }
    );
  } else {
    // After animation completes
    y = height + 100; // Off-screen
  }
  
  // Calculate opacity
  const opacity = frame < totalDuration ? 
    interpolate(
      frame,
      [0, appearDuration, appearDuration + stayDuration, totalDuration],
      [0, 1, 1, 0],
      { extrapolateRight: 'clamp' }
    ) : 0;
  
  // Only render if visible
  if (opacity <= 0) return null;
  
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        transform: `translateY(${y - height}px)`,
        opacity,
        textAlign: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
    >
      <div
        style={{
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          fontWeight: 'bold',
          fontSize: `${Math.min(width, height) * 0.04}px`,
          display: 'inline-block',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        {text || 'Section'}
      </div>
    </div>
  );
};

interface ZoomedContainerProps {
  children: React.ReactNode;
  initialZoom: boolean;
}

// Create a separate component for zoom functionality
const ZoomedContainerBase = ({ children, initialZoom }: ZoomedContainerProps) => {
  const frame = useCurrentFrame();
  const zoomDuration = 10; // frames

  const scale = frame < zoomDuration
    ? interpolate(frame, [0, zoomDuration], [1.7, 1], { 
        extrapolateRight: 'clamp',
        easing: easeOutBack
      })
    : 1;

  return (
    <AbsoluteFill style={{ transform: `scale(${initialZoom ? scale : 1})`, transformOrigin: 'center center' }}>
      {children}
    </AbsoluteFill>
  );
};

ZoomedContainerBase.displayName = 'ZoomedContainer';

const ZoomedContainer = React.memo(ZoomedContainerBase);

export const MyCompositionHtmlComponent: React.FC<HtmlProp> = ({
  htmlData,
  jsonData,
  scriptDetails,
  width,
  height,
  transitionDurationInFrames,
  transitionAnimationProp,
  isDownload,
  burstImagesTransitionAnimationArray,
  videoStyle,
  aiAvatar,
}) => {
  // const frame = useCurrentFrame();
  // const { fps } = useVideoConfig();
  const initialZoom = Boolean(scriptDetails?.initialZoom);
  const audioFade = scriptDetails?.audioFade || false


  const customTransitionEffect =
    scriptDetails?.customTransitionSoundurl ||
    'https://qa-dev-quickads-project.quickads.ai/image_projects/da764da2-2982-49be-88bd-eca414c4f5d7/assets/audio/e4cdb276-d1ad-4d81-91ae-3182d55c4099/audio.mp3'

  /********* Get Caption styles and check if we need to show them or not *********/
  const captionsTextStyle = scriptDetails?.template
  const showSubtitles = scriptDetails?.showSubtitles

  const newCaptionStyle = {
    ...captionsTextStyle,
    position: captionsTextStyle?.position - 100,
  }
  // const verticalCaptionStyle = {
  //   ...captionsTextStyle,
  //   position: captionsTextStyle?.position + 100,
  // };
  const verticalCaptionStyle = {
    ...captionsTextStyle,
    position: captionsTextStyle?.position,
  }
  // const newcaptionStyle=captionsTextStyle
  // newcaptionStyle['position']=208
  /********* Get Background music URL and volume and voiceover volume *********/

  const backgroundMusicUrl = scriptDetails?.backgroundMusic?.url.startsWith('audio/')
    ? staticFile(scriptDetails?.backgroundMusic?.url)
    : scriptDetails?.backgroundMusic?.url
  const proxyBackgroundUrl =
    'https://qa-dev-quickads-project.quickads.ai/image_projects/195c0a0b-5507-4fdb-9d94-521270353a66/assets/audio/251687ab-4155-4ee3-86a6-dc5bc70fd0b2/audio.mp3'
  const backgroundMusicVolume = scriptDetails?.backgroundMusic?.backgroundVolume || 1
  const backgroundmusicDurationInMili = scriptDetails?.backgroundMusic?.duration * 1000
  const backgroundmusicInframes = millisecondsToFrames(backgroundmusicDurationInMili, 30)
  // const voiceOverVolume = scriptDetails?.aiVoiceVolume
  //   ? scriptDetails?.aiVoiceVolume
  //   : 0.7;
  const voiceOverVolume = scriptDetails?.aiVoiceVolume ?? 0.7

  // const backgroundSound = scriptDetails?.backgroundSound || true
  const backgroundSound = scriptDetails?.backgroundSound ?? true
  const transitionSound = scriptDetails?.transitionSound ?? false
  const aiVoiceSpeed =  1
  /********* Store last screen index for transition check *********/
  const lastScreen = scriptDetails?.data.length - 1
  const overlayAnimation = scriptDetails?.overlayAnimation || false
  let accumulatedFrames = 0

  let pageIndex = 0

  const accumulatedFramesArray: any = []

  const isThumbnailVisible = scriptDetails?.thumbnailTextVisible ?? true

  return (
    <ZoomedContainer initialZoom={initialZoom}>
      {/* Load all fonts */}
      <FontLoader />
      
      {/* Add SoundEffects component at the top level */}
      {scriptDetails?.sfxs && (
        <SoundEffects 
          sfxs={scriptDetails.sfxs}
          // isDownload={isDownload}
        />
      )}
      
      {/* {initialZoom && frame < zoomDuration && (
        <Audio
          src="https://qa-dev-quickads-project.quickads.ai/image_projects/94718394-8ef1-4da2-99de-333967f9fbde/assets/audio/0a484492-5846-47a3-b2e7-633baa360031/audio.mp3"
          volume={audioVolume}
        />
      )} */}
      <TransitionSeries>
        {(scriptDetails?.showThumbnail ?? true) && (
          <TransitionSeries.Sequence key={`thumbnail-sequence`} durationInFrames={1} premountFor={30}>
            <RenderPosterComponentTwo
              height={height}
              width={width}
              thumbnailImage={scriptDetails?.thumbnailImage}
              thumbnailMessage={
                scriptDetails?.thumbnailText || scriptDetails?.data[0].textOverlay || scriptDetails?.data[1].textOverlay
              }
              isThumbnailVisible={isThumbnailVisible}
            />
          </TransitionSeries.Sequence>
        )}

        {scriptDetails?.data.map((page: any, index: number) => {
          /********* Get the page JSON and HTML if Simple video -> 1 page === 1 JSON page. Indexes are ordered between both objects *********/
          const jsonPage = jsonData.pages[index]
          const pageHtml = htmlData[index]
          const pauseDurationInFrames = 60
          /********* Get the JSON and HTML of all pages that are linked to this page object. 1 page object ===  page.images.length No of pages *********/
          const jsonPages: any = []
          const htmlPages: any = []
          let durationInFrames: any = 0
          const transitionFunction: any =
            transitionAnimationsDropDown.find((item) => {
              // First check if there's a transition defined for this page
              if (page?.transition?.animation) {
                return item.value === page.transition.animation;
              }
              // If no page transition, use the global transition prop
              return item.value === transitionAnimationProp;
            })?.functions || transitionAnimationsDropDown[0].functions
          const sectionTransitionSound = page?.transition?.url || '';
          const sectionTransitionSoundEnable = page?.transition?.transitionSound === undefined ? Boolean(transitionSound) : Boolean(page?.transition?.transitionSound);
          if (videoStyle === 'Burst') {
            /********* Loop over pages and add them to an array of JSON and HTML pages array until the loop matches pageIndex + page.images.length *********/
            for (let i = pageIndex; i < pageIndex + page.images.length; i++) {
              jsonPages.push(jsonData.pages[i])
              htmlPages.push(htmlData[i])

              if (jsonData.pages[i]?.duration) {
                durationInFrames += (jsonData.pages[i]?.duration / 1000) * fps
              }
            }
            pageIndex += page.images.length
          } else if (videoStyle === 'Simple') {
            jsonPages.push(jsonData.pages[pageIndex])
            htmlPages.push(htmlData[pageIndex])
            if (jsonData.pages[pageIndex]?.duration) {
              durationInFrames += (jsonData.pages[pageIndex].duration / 1000) * fps
            }

            pageIndex++
          }
          const backuptransitionSoundEffect = transitionAnimationsDropDown.find((item) => {
            // First check if there's a transition defined for this page
            if (page?.transition?.animation) {
              return item.value === page.transition.animation;
            }
            // If no page transition, use the global transition prop
            return item.value === transitionAnimationProp;
          })?.url || `audio/transitions_/${transitionAnimationProp}.mp3`
          const transitionSoundEffect = scriptDetails?.customtransitionSound
          ? customTransitionEffect
          : page?.transition?.url || backuptransitionSoundEffect
         
          const sequenceKey: string = generateUniqueId()
          const transitionKey: string = generateUniqueId()

          const captions = page?.captions ? page?.captions[0]?.words : undefined
          
          const voiceOver = page?.sounds ? page?.sounds[0]?.url : undefined
          const voiceOverDuration = page?.sounds ? page?.sounds[0]?.audioDuration : undefined

          const audioDelay = fps * delayInSeconds

          const voiceOverDurationFrame = Math.round((voiceOverDuration + delayInSeconds) * fps)

          // Page start and end frames
          accumulatedFramesArray.push(accumulatedFrames)
          const pageStartFrame = accumulatedFrames
          const pageEndFrame = accumulatedFrames + durationInFrames + transitionDurationInFrames / 10 + ((scriptDetails?.topicIntroduction) ? pauseDurationInFrames : 0)
          accumulatedFrames += durationInFrames
          if (pageEndFrame >= backgroundmusicInframes) {
            accumulatedFrames = 0
          }
          const volume = backgroundMusicVolume ? backgroundMusicVolume : 0.5

          const transitionBusrtAnimations = burstImagesTransitionAnimationArray[index]
          const remainingDuration = durationInFrames
          const adjustedTransitionDuration = transitionDurationInFrames
         
          // const adjustedTransitionDuration = Math.min(
          //   transitionDurationInFrames,
          //   remainingDuration
          // );

          // const hasMuxStream = jsonPage?.children?.some((child: any) => child?.src?.includes('stream.mux'));
          if (durationInFrames == 0) {
            durationInFrames = 1
          }
          return (
            <>
              <TransitionSeries.Sequence
                key={`sequence-${index}-${sequenceKey}`}
                durationInFrames={durationInFrames + adjustedTransitionDuration + ((scriptDetails?.topicIntroduction) ? pauseDurationInFrames : 0)}
                premountFor={90}
              >
                {/* Add the animated block type indicator */}
                {(scriptDetails?.topicIntroduction) && (
                  <Sequence from={0} durationInFrames={pauseDurationInFrames}>
                    <AnimatedBlockType
                      text={page.adBlockType}
                      width={width}
                      height={height}
                    />
                  </Sequence>
                )}


                {/* Add background Audio in Transition so we can add sound fading using interpolation */}
                {backgroundSound && (
                  <PremountedAudio
                    src={backgroundMusicUrl || proxyBackgroundUrl}
                    startFrom={pageStartFrame}
                    endAt={pageEndFrame}
                    volume={(f) => {
                      // If IDEA or Blog flow and in the intro period (first 90 frames), increase volume
                      if ((scriptDetails?.topicIntroduction) && f < pauseDurationInFrames) {
                        return backgroundMusicVolume * 1.5; // 50% louder during intro
                      }
                      
                      // Regular volume logic for rest of the scene
                      if (audioFade) {
                        if (f <= audioDelay) {
                          return interpolate(
                            f,
                            [0, audioDelay],
                            [backgroundMusicVolume, backgroundMusicVolume / 10],
                            {
                              extrapolateLeft: 'clamp',
                              extrapolateRight: 'clamp',
                            }
                          )
                        } else if (f >= voiceOverDurationFrame) {
                          if (index === lastScreen) {
                            return interpolate(
                              f,
                              [voiceOverDurationFrame, durationInFrames + transitionDurationInFrames],
                              [backgroundMusicVolume, backgroundMusicVolume / 10],
                              {
                                extrapolateLeft: 'clamp',
                                extrapolateRight: 'clamp',
                              }
                            )
                          } else {
                            // Ensure the input range is in ascending order
                            const startRange = voiceOverDurationFrame;
                            const endRange = durationInFrames + transitionDurationInFrames;
                            
                            // If end range is less than or equal to start range, just return the constant volume
                            if (endRange <= startRange) {
                              return backgroundMusicVolume;
                            }
                            
                            return interpolate(
                              f,
                              [startRange, endRange],
                              [backgroundMusicVolume, backgroundMusicVolume],
                              {
                                extrapolateLeft: 'clamp',
                                extrapolateRight: 'clamp',
                              }
                            )
                          }
                        } else if (f >= audioDelay && f <= voiceOverDurationFrame) {
                          return backgroundMusicVolume / 10
                        }
                      }
                      return backgroundMusicVolume
                    }}
                    premountFor={90}
                  />
                )}

                {videoStyle === 'Burst' ? (
                  // If the videoStyle is Burst then render the Burst video where multiple images are played on one scene / Ad block type / Script Page
                  // This is a sequence of images with a transition between each image / jsonPage
                  <TransitionSeries>
                    {jsonPages?.map((jsonPage: any, jsonIndex: number) => {
                      // Get a random transition animation function for each burst image
                      const transitionAnimationFunction: any =
                      burstImageTransitionFunctions[
                          Math.floor(Math.random() * burstImageTransitionFunctions.length)
                        ].function;
                      
                      //  Calculate the number of frames for each page / image
                      let pageFrames = 0;
                      if (jsonPage?.duration) {
                        pageFrames = Math.floor((jsonPage?.duration / 1000) * fps) + 20;
                      } else {
                        pageFrames = 0;
                      }
                  
                      function removeElementsByIds(html: string, idsToRemove: any) {
                        // Parse the HTML string into a DOM object
                        const parser = new DOMParser()
                        const doc = parser.parseFromString(html, 'text/html')

                        // Loop through the array of IDs and remove each corresponding element
                        idsToRemove.forEach((id: any) => {
                          const elementToRemove = doc.getElementById(id)
                          if (elementToRemove) {
                            elementToRemove.remove()
                          }
                        })

                        // Return the updated HTML as a string
                        return doc.body.innerHTML
                      }
                      let aiAvatar = false
                      // const pageHtml=htmlPages[index]
                      const imageids: any = []

                      if (
                        page?.aiAvatarProps?.avatarWithBg ||
                        page?.aiAvatarProps?.avatar ||
                        page?.aiAvatarProps?.avatarWithWebsite
                      )
                        jsonPage?.children.map((child: any, index: number) => {
                          if (child.type == 'image') {
                            imageids.push(child.id)
                            aiAvatar = true
                          } else if (child.type == 'video') {
                            if (child?.custom?.aiAvatar) {
                              aiAvatar = child?.custom?.aiAvatar || false
                              return
                            }
                          }
                        })

                      // const pageHtml=removeElementsByIds(htmlPages[index],imageids)
                      let pageHtml = removeVideoTags(htmlPages[jsonIndex])
                      if (aiAvatar) {
                        pageHtml = removeElementsByIds(pageHtml, imageids)
                      }
                    

                      return (
                        <>
                          <TransitionSeries.Sequence
                            key={`transitionSeriesSequence-${jsonIndex}`}
                            durationInFrames={pageFrames + 20}
                            premountFor={90}
                          >
                            {/* {jsonPage?.children && ( */}
                              <SinglePage
                                key={jsonIndex}
                                pageHtml={pageHtml}
                                width={width}
                                height={height}
                                pageData={jsonPage}
                                isDownload={isDownload}
                                aiAvatar={
                                  page?.aiAvatarProps?.avatarWithBg ||
                                  page?.aiAvatarProps?.avatar ||
                                  page?.aiAvatarProps?.avatarWithWebsite
                                }
                                overlayAnimation={overlayAnimation}
                                webScroll={page?.aiAvatarProps?.avatarWithWebsite}
                                premountFor={90}
                                sectionLength={scriptDetails?.data.length}
                                ctaData={scriptDetails?.ctaObject}
                                sectionIndex={index}
                              />
                            {/* )} */}
                            {!jsonPage?.children && (
                              <AbsoluteFill>
                                <div>{''}</div>
                              </AbsoluteFill>
                            )}
                            {/* <Audio
                                src={staticFile('audio/slideEffect.mp3')}
                                volume={0.02}
                               
                              /> */}
                          </TransitionSeries.Sequence>
                          
                          {/* Only add transition if not the last element */}
                          {jsonIndex < jsonPages.length - 1 && (
                            <Transition
                              key={`transitionSeriesTransition-${jsonIndex}`}
                              presentation={transitionAnimationFunction}
                              timing={springTiming({
                                config: { damping: 100, stiffness: 70 },
                                durationInFrames: 25,
                                durationRestThreshold: 0.0002,
                              })}
                            />
                          )}
                        </>
                      )
                    })}
                  </TransitionSeries>
                ) : (
                  // If the videoStyle is not Burst then render the Simple video where one Audio is played on one scene
                  <SinglePage
                    pageHtml={pageHtml}
                    width={width}
                    height={height}
                    pageData={jsonPage}
                    isDownload={isDownload}
                    aiAvatar={
                      page?.aiAvatarProps?.avatarWithBg ||
                      page?.aiAvatarProps?.avatar ||
                      page?.aiAvatarProps?.avatarWithWebsite
                    }
                    overlayAnimation={overlayAnimation}
                    webScroll={page?.aiAvatarProps?.avatarWithWebsite}
                    premountFor={90}
                  />
                )}
                {/* Add a sequence series for AI Voice audio and subtitles */}
                <Sequence
                  from={(scriptDetails?.topicIntroduction) ? pauseDurationInFrames + (0.5 * fps) : 0.5 * fps} // Delay for IDEA and Blog flows
                  durationInFrames={durationInFrames + transitionDurationInFrames} // Don't subtract frames, keep full duration
                >
                  {/* // If AI Voice audio is available, add it to the sequence */}

                  {!isDownload ? (
                    <>
                      {voiceOver && (
                        <PremountedVoiceOver
                          src={voiceOver}
                          startFrom={0}
                          volume={voiceOverVolume}
                          playbackRate={aiVoiceSpeed}
                          premountFor={90}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {!(
                        (page?.aiAvatarProps?.avatarWithBg ||
                         page?.aiAvatarProps?.avatar ||
                         page?.aiAvatarProps?.avatarWithWebsite 
                        //  || 
                        //  !jsonPages.some((jsonPage: any) => jsonPage?.custom?.aiAvatar === true
                        // )
                        )
                      ) && (
                        <>
                          {voiceOver && (
                            <PremountedVoiceOver
                              src={voiceOver}
                              startFrom={0}
                              volume={voiceOverVolume}
                              playbackRate={aiVoiceSpeed}
                              premountFor={90}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                  {/* // If Captions are enabled and available, add them to the sequence */}

                  {captions && captionsTextStyle && showSubtitles && (
                    <AnimatedSubtitles
                      captions={captions}
                      width={width}
                      height={height}
                      isDownload={isDownload}
                      style={width < height ? verticalCaptionStyle : newCaptionStyle}
                    // style={newCaptionStyle}
                    />
                  )}
                </Sequence>
              </TransitionSeries.Sequence>

              {/*
               Don't Add the transition series for the last scene as it will show a blank white screen in the end otherwise
               Add a transition series for the transition between scenes / Ad Block sections pages
               */}
         
              {index < scriptDetails?.data.length - 1 && (
                sectionTransitionSoundEnable ? (
                  <Transition
                    key={`sequence-${index}-${transitionKey}`}
                    presentation={addSound(
                      transitionFunction,
                      scriptDetails?.customtransitionSound ? transitionSoundEffect : sectionTransitionSound ? sectionTransitionSound : transitionSoundEffect
                    )}
                    timing={springTiming({
                      config: { damping: 25, stiffness: 70 },
                      durationInFrames: adjustedTransitionDuration,
                      durationRestThreshold: 0.0002,
                    })}
                  />
                ) : (
                  <Transition
                    key={`sequence-${index}-${transitionKey}`}
                    presentation={transitionFunction}
                    timing={springTiming({
                      config: { damping: 100, stiffness: 70 },
                      durationInFrames: adjustedTransitionDuration,
                      durationRestThreshold: 0.0002,
                    })}
                  />
                )
              )}
            </>
          )
        })}
      </TransitionSeries>
    </ZoomedContainer>
  )
}

export const MyCompositionHtml = React.memo(MyCompositionHtmlComponent)

// if (index !== scriptIndex) return null;
